import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Candle } from "@/components/Candle";
import { Logo } from "@/components/Logo";
import { AuthDialog } from "@/components/AuthDialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Flame, Shield, LogOut, LogIn } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { RisingMemories } from "@/components/RisingMemories";
import { useTranslation } from "react-i18next";

export default function Home() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  useEffect(() => {
    if (params.get("auth") === "register" && !user) {
      setAuthMode("register");
      setAuthOpen(true);
      params.delete("auth");
      setParams(params, { replace: true });
    }
  }, [params, user, setParams]);

  const startCandle = () => {
    if (user) navigate("/creer");
    else { setAuthMode("login"); setAuthOpen(true); }
  };

  return (
    <div className={`relative flex min-h-[100dvh] flex-col bg-black ${user ? "pb-[calc(86px+env(safe-area-inset-bottom))]" : ""}`}>
      {/* top bar (fixe) */}
      <div className="sticky top-0 z-30 flex items-center justify-center bg-black px-5 py-4" style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
        <div className="absolute left-4 top-1/2 -translate-y-1/2">
          <LanguageSwitcher />
        </div>
        <Logo size="lg" />
        <div className="absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {user && <NotificationsBell />}
          {user?.role === "admin" && (
            <Button variant="ghost" size="sm" className="rounded-full text-sky-400" onClick={() => navigate("/admin")} data-testid="home-admin">
              <Shield size={16} />
            </Button>
          )}
          {user ? (
            <Button variant="ghost" size="sm" className="rounded-full text-muted-foreground" onClick={() => logout()} data-testid="home-logout">
              <LogOut size={16} />
            </Button>
          ) : null}
        </div>
      </div>

      {/* centered candle */}
      <div className="flex flex-1 flex-col items-center justify-center px-6 text-center fade-in">
        <div className="relative my-1 sm:my-2">
          <Candle progress={1} size="min(420px, 32vh)" />
          <RisingMemories />
        </div>
        <h1 className="mt-1 max-w-md text-2xl font-medium leading-tight sm:mt-2 sm:text-3xl lg:text-4xl">
          {t("home.title")}
        </h1>
        <p className="mt-2 max-w-md text-base font-normal leading-relaxed text-white sm:mt-3 sm:text-lg lg:text-xl" data-testid="home-subtitle">
          {t("home.subtitle")}
        </p>
      </div>

      {/* CTA */}
      <div className="px-6 pb-4 sm:pb-6">
        <Button
          onClick={startCandle}
          className="mx-auto flex h-14 w-full max-w-md items-center justify-center rounded-full text-lg font-semibold shadow-[0_4px_24px_0_rgba(56,189,248,0.45)] transition-transform hover:scale-[1.02]"
          data-testid="light-candle-btn"
        >
          <Flame size={24} className="mr-2" /> {t("home.cta")}
        </Button>
        {!user && (
          <Button
            variant="outline"
            onClick={() => { setAuthMode("login"); setAuthOpen(true); }}
            className="mx-auto mt-3 flex h-12 w-full max-w-md items-center justify-center rounded-full border-white/25 bg-white/5 text-base font-medium text-white hover:bg-white/10 sm:h-14 sm:text-lg"
            data-testid="home-login-btn"
          >
            <LogIn size={20} className="mr-2" /> {t("auth.login", "Se connecter")}
          </Button>
        )}
        <p className="mt-3 text-center text-sm text-muted-foreground" data-testid="cta-subtext">{t("home.ctaSub")}</p>
        <p className="mt-1 text-center text-sm text-sky-400" data-testid="cta-free-text">{t("home.ctaFree")}</p>
        <button
          onClick={() => navigate("/toussaint")}
          data-testid="home-toussaint-link"
          className="mx-auto mt-2 flex items-center justify-center gap-1.5 text-center text-xs text-amber-300/90 transition-colors hover:text-amber-200"
        >
          <Flame size={12} /> {t("tss.offerTitle")} — {t("tss.offerPrice")}
        </button>
      </div>

      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} onSuccess={(mode) => navigate(mode === "login" ? "/" : "/creer")} />
    </div>
  );
}
